import { Suspense, lazy, useEffect, useState } from "react";
import Header from "./components/Header";
import Landing from "./components/Landing";
import Footer from "./components/Footer";
import { cachedGet } from "./api";
import { getToken } from "./session";
import useHashRoute, { isMemberRoute } from "./useHashRoute";

// Only the landing page ships in the main bundle; everything else loads when it is opened.
const AuthPage = lazy(() => import("./components/AuthPage"));
const MemberApp = lazy(() => import("./member/MemberApp"));
const PublicPackage = lazy(() => import("./member/Packages").then((m) => ({ default: m.PublicPackage })));
const FaqPage = lazy(() => import("./publicPages").then((m) => ({ default: m.FaqPage })));
const ContactPage = lazy(() => import("./publicPages").then((m) => ({ default: m.ContactPage })));
const EmailTest = lazy(() => import("./publicPages").then((m) => ({ default: m.EmailTest })));
const CmsPage = lazy(() => import("./publicPages").then((m) => ({ default: m.CmsPage })));

const pageFallback = <div className="page-loading" aria-busy="true" />;

function Announcement() {
  const [visible, setVisible] = useState(true);
  const [note, setNote] = useState(null);

  useEffect(() => {
    cachedGet("/api/v1/public/announcements")
      .then((result) => setNote(result.data?.[0] || null))
      .catch(() => {});
  }, []);

  if (!visible || !note) return null;

  return (
    <div className="announce">
      <p>
        <span>{note.title}.</span> {note.body}
      </p>
      <button type="button" onClick={() => setVisible(false)}>
        Dismiss
      </button>
    </div>
  );
}

function Consent() {
  const [choice, setChoice] = useState("");
  if (choice) return null;

  return (
    <div className="consent" role="dialog" aria-label="Site consent">
      <p>Essential cookies keep this site working. Optional analytics stay off until they are configured.</p>
      <div>
        <button type="button" className="btn btn-ghost" onClick={() => setChoice("essential")}>
          Essential only
        </button>
        <button type="button" className="btn btn-primary" onClick={() => setChoice("accept")}>
          Accept
        </button>
      </div>
    </div>
  );
}

const authPages = ["login", "signup", "register", "forgot-password", "reset-password"];

export default function App() {
  const route = useHashRoute();
  const isAuth = authPages.includes(route.page);
  const isMember = isMemberRoute(route);

  useEffect(() => {
    if (getToken() && (route.page === "login" || route.page === "signup" || route.page === "register")) {
      window.location.hash = "#/dashboard";
    }
  }, [route.page]);

  useEffect(() => {
    if (isAuth || isMember || route.page !== "home") return undefined;
    const id = window.location.hash.replace("#", "");
    if (id && !id.startsWith("/")) document.getElementById(id)?.scrollIntoView();
    return undefined;
  }, [isAuth, isMember, route.page]);

  if (isAuth) {
    return (
      <Suspense fallback={pageFallback}>
        <AuthPage mode={route.page} params={route.params} />
      </Suspense>
    );
  }
  if (isMember) {
    return (
      <Suspense fallback={pageFallback}>
        <MemberApp route={route} />
      </Suspense>
    );
  }

  let page = <Landing />;
  if (route.page === "faq") page = <FaqPage />;
  if (route.page === "contact") page = <ContactPage />;
  if (route.page === "email-test") page = <section className="section"><div className="wrap narrow"><EmailTest /></div></section>;
  if (route.page === "pages") page = <CmsPage slug={route.parts[1]} />;
  if (route.page === "packages" && route.parts[1]) page = <PublicPackage id={route.parts[1]} />;

  return (
    <>
      <a className="skip" href="#home">
        Skip to content
      </a>
      {route.page === "home" && <Announcement />}
      <Header />
      <Suspense fallback={pageFallback}>{page}</Suspense>
      <Footer />
      <Consent />
    </>
  );
}
